import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Printer,
  Plus,
  Trash2,
  Edit,
  Calendar,
  User,
  PenLine,
  ClipboardCheck,
  CheckCircle2,
  RotateCcw,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchableUserSelect from '../components/SearchableUserSelect';
import SignaturePad from '../components/SignaturePad';
import Podpis from '../components/Podpis';
import toast from 'react-hot-toast';
import kadryService from '../services/kadryService';
import { numberToWords } from '../utils/numberToWords';
import { formatSignerNameUnderPad, formatUserFullName } from '../utils/userDisplayName';
import logoImage from '../assets/logo.png';

/** Suma kosztów przejazdu z tabeli (zł + gr → wartość dziesiętna) */
function sumaKosztowPrzejazduZListy(wyjazdy) {
  let totalGr = 0;
  for (const wp of wyjazdy || []) {
    const zl = parseFloat(wp.kosztPrzejazdu?.zl) || 0;
    const gr = parseFloat(wp.kosztPrzejazdu?.gr) || 0;
    totalGr += Math.round(zl * 100) + Math.round(gr);
  }
  return totalGr / 100;
}

function formatujSumeWplatyZwrotu(kwota) {
  const n = Number(kwota) || 0;
  const totalGr = Math.round(Math.abs(n) * 100);
  const zl = Math.floor(totalGr / 100);
  const gr = totalGr % 100;
  const sign = n < 0 ? '−' : '';
  return `${sign}${zl} zł ${String(gr).padStart(2, '0')} gr`;
}

/** Data do wydruku A4 */
function fmtDateVal(val) {
  if (!val) return '—';
  try {
    const x = val instanceof Date ? val : new Date(val);
    if (Number.isNaN(x.getTime())) return '—';
    return x.toLocaleDateString('pl-PL');
  } catch {
    return '—';
  }
}

const WniosekDelegacyjny = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(!id);
  const [activeSection, setActiveSection] = useState('polecenie'); // 'polecenie' | 'rachunek' | 'akceptacje'
  const [podpisPrzełożonego, setPodpisPrzełożonego] = useState('');
  /** Po ?focus= — puls przy polu podpisu */
  const [podpisHighlight, setPodpisHighlight] = useState(null); // 'merytoryczny' | 'formalny' | null
  const merytorycznyPodpisRef = useRef(null);
  const formalnyBlokRef = useRef(null);
  const [zwrotModalOpen, setZwrotModalOpen] = useState(false);
  const [zwrotPolecenieModalOpen, setZwrotPolecenieModalOpen] = useState(false);
  const [komentarzZwrotu, setKomentarzZwrotu] = useState('');
  const navigate = useNavigate();

  // Pobierz wniosek jeśli edytujemy istniejący
  const { data: wniosekData, isLoading: isLoadingWniosek } = useQuery(
    ['wniosek-delegacyjny', id],
    () => kadryService.getWniosekDelegacyjny(id),
    {
      enabled: !!id,
      onSuccess: (data) => {
        if (data?.wniosek) {
          const w = data.wniosek;
          setFormData({
            // Podstawowe dane
            pracownikImie: w.pracownikImie || '',
            pracownikNazwisko: w.pracownikNazwisko || '',
            stanowisko: w.stanowisko || '',
            dataZlozenia: w.dataZlozenia ? new Date(w.dataZlozenia).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            celDelegacji: w.celDelegacji || '',
            miejsceDelegacji: w.miejsceDelegacji || '',
            dataOd: w.dataOd ? new Date(w.dataOd).toISOString().split('T')[0] : '',
            dataDo: w.dataDo ? new Date(w.dataDo).toISOString().split('T')[0] : '',
            liczbaDni: w.liczbaDni || '',
            samochodSluzbowyNumerRejestracji: (() => {
              if (w.samochodSluzbowyNumerRejestracji) return w.samochodSluzbowyNumerRejestracji;
              const s = w.srodekTransportu && String(w.srodekTransportu);
              if (!s) return '';
              const m = s.match(/nr\.?\s*rej\.?\s*(.+)$/i);
              return m ? m[1].trim() : '';
            })(),
            telefonKontaktowy: w.telefonKontaktowy || '',
            podpisPracownika: w.podpisPracownika || '',
            uzasadnienie: w.uzasadnienie || '',
            koszty: w.koszty || '',
            
            // Polecenie wyjazdu
            numerPolecenia: w.numerPolecenia || '',
            dataPolecenia: w.dataPolecenia
              ? new Date(w.dataPolecenia).toISOString().split('T')[0]
              : (w.dataZlozenia ? new Date(w.dataZlozenia).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]),
            dataAkceptacjiPolecenia: w.dataAkceptacjiPolecenia ? new Date(w.dataAkceptacjiPolecenia).toISOString().split('T')[0] : '',
            podpisAkceptujacego: w.podpisAkceptujacego || '',
            przełożony: (w.przełożony && typeof w.przełożony === 'object' && w.przełożony._id)
              ? w.przełożony._id
              : (w.przełożony || ''),
            podpisWysylajacego: w.podpisWysylajacego || '',
            stwierdzeniePobytu: w.stwierdzeniePobytu || '',
            
            // Zaliczka
            zaliczkaKwota: w.zaliczkaKwota || '',
            zaliczkaSlownie: w.zaliczkaSlownie || '',
            zaliczkaZatwierdzonaKwota: w.zaliczkaZatwierdzonaKwota || '',
            zaliczkaZatwierdzonaSlownie: w.zaliczkaZatwierdzonaSlownie || '',
            zrodloWyplaty: w.zrodloWyplaty || '',
            
            // Rachunek kosztów
            wyjazdyPrzyjazdy: w.wyjazdyPrzyjazdy || [],
            
            // Sprawdzenie
            sprawdzenieMerytoryczne: {
              data: w.sprawdzenieMerytoryczne?.data
                ? new Date(w.sprawdzenieMerytoryczne.data).toISOString().split('T')[0]
                : '',
              podpis: w.sprawdzenieMerytoryczne?.podpis || '',
              imieNazwisko: w.sprawdzenieMerytoryczne?.imieNazwisko || ''
            },
            sprawdzenieFormalne: {
              data: w.sprawdzenieFormalne?.data
                ? new Date(w.sprawdzenieFormalne.data).toISOString().split('T')[0]
                : '',
              podpis: w.sprawdzenieFormalne?.podpis || '',
              imieNazwisko: w.sprawdzenieFormalne?.imieNazwisko || ''
            },
            
            // Zatwierdzenie
            zatwierdzenieKwota: w.zatwierdzenieKwota || '',
            zatwierdzenieSlownie: w.zatwierdzenieSlownie || '',
            zatwierdzenieData: w.zatwierdzenieData ? new Date(w.zatwierdzenieData).toISOString().split('T')[0] : '',
            zatwierdzeniePodpisy: w.zatwierdzeniePodpisy || '',
            zatwierdzenieImieNazwiskoAkceptujacego: w.zatwierdzenieImieNazwiskoAkceptujacego || '',
            
            // Podsumowanie kosztów
            kosztyRyczaltyDojazdy: w.kosztyRyczaltyDojazdy || '',
            kosztyDojazdyUdokumentowane: w.kosztyDojazdyUdokumentowane || '',
            kosztyRazemPrzejazdy: w.kosztyRazemPrzejazdy || '',
            kosztyDiety: w.kosztyDiety || '',
            kosztyNoclegiRachunki: w.kosztyNoclegiRachunki || '',
            kosztyNoclegiRyczalt: w.kosztyNoclegiRyczalt || '',
            kosztyInneWydatki: w.kosztyInneWydatki || '',
            kosztyOgolem: w.kosztyOgolem || '',
            kosztyOgolemSlownie: w.kosztyOgolemSlownie || '',
            
            // Rozliczenie
            kwitujeOdbiorKwota: w.kwitujeOdbiorKwota || '',
            kwitujeOdbiorSlownie: w.kwitujeOdbiorSlownie || '',
            kwitujeOdbiorData: w.kwitujeOdbiorData ? new Date(w.kwitujeOdbiorData).toISOString().split('T')[0] : '',
            kwitujeOdbiorPodpis: w.kwitujeOdbiorPodpis || '',
            zalaczamDowodow: w.zalaczamDowodow || '',
            pobranoZaliczke: w.pobranoZaliczke || '',
            doWyplatyZwrotu: w.doWyplatyZwrotu || '',
            rachunekDataPrzedlozenia: w.rachunekDataPrzedlozenia
              ? new Date(w.rachunekDataPrzedlozenia).toISOString().split('T')[0]
              : '',
            rachunekPodpisPracownika: w.rachunekPodpisPracownika || ''
          });
        }
      }
    }
  );

  const { data: usersData, isLoading: usersLoading } = useQuery(
    ['kadry-users', 'wniosek-delegacyjny'],
    () => kadryService.getUsers(),
    { staleTime: 5 * 60 * 1000, enabled: isEditing }
  );

  React.useEffect(() => {
    if (id && (wniosekData?.widokWniosku === 'adresat' || wniosekData?.widokWniosku === 'hr_ks')) {
      setIsEditing(false);
    }
  }, [id, wniosekData?.widokWniosku]);

  // Po podpisie przełożonego lub zwrocie od HR: autor ma uzupełnić / poprawić rachunek
  React.useEffect(() => {
    if (!id || !wniosekData?.wniosek || wniosekData.widokWniosku !== 'autor') return;
    const w = wniosekData.wniosek;
    if (
      w.podpisPrzełożonego &&
      (w.oczekujeNaRachunekKosztow || w.oczekujeNaPoprawkeRachunku) &&
      !w.rachunekKosztowUzupełniony
    ) {
      setIsEditing(true);
      setActiveSection('rachunek');
    }
  }, [id, wniosekData]);

  React.useEffect(() => {
    if (wniosekData?.wniosek?.akceptacjaFormalnaZakonczona) setIsEditing(false);
  }, [wniosekData?.wniosek?.akceptacjaFormalnaZakonczona]);

  const [formData, setFormData] = useState({
    // Podstawowe dane
    pracownikImie: user?.firstName || '',
    pracownikNazwisko: user?.lastName || '',
    stanowisko: user?.position || '',
    dataZlozenia: new Date().toISOString().split('T')[0],
    celDelegacji: '',
    miejsceDelegacji: '',
    dataOd: '',
    dataDo: '',
    liczbaDni: '',
    samochodSluzbowyNumerRejestracji: '',
    telefonKontaktowy: user?.phone || '',
    podpisPracownika: '',
    uzasadnienie: '',
    koszty: '',
    
    // Polecenie wyjazdu
    numerPolecenia: '',
    dataPolecenia: new Date().toISOString().split('T')[0],
    dataAkceptacjiPolecenia: '',
    podpisAkceptujacego: '',
    przełożony: '',
    podpisWysylajacego: '',
    stwierdzeniePobytu: '',
    
    // Zaliczka
    zaliczkaKwota: '',
    zaliczkaSlownie: '',
    zaliczkaZatwierdzonaKwota: '',
    zaliczkaZatwierdzonaSlownie: '',
    zrodloWyplaty: '',
    
    // Rachunek kosztów
    wyjazdyPrzyjazdy: [],
    
    // Sprawdzenie
    sprawdzenieMerytoryczne: { data: '', podpis: '', imieNazwisko: '' },
    sprawdzenieFormalne: { data: '', podpis: '', imieNazwisko: '' },
    
    // Zatwierdzenie
    zatwierdzenieKwota: '',
    zatwierdzenieSlownie: '',
    zatwierdzenieData: '',
    zatwierdzeniePodpisy: '',
    zatwierdzenieImieNazwiskoAkceptujacego: '',
    
    // Podsumowanie kosztów
    kosztyRyczaltyDojazdy: '',
    kosztyDojazdyUdokumentowane: '',
    kosztyRazemPrzejazdy: '',
    kosztyDiety: '',
    kosztyNoclegiRachunki: '',
    kosztyNoclegiRyczalt: '',
    kosztyInneWydatki: '',
    kosztyOgolem: '',
    kosztyOgolemSlownie: '',
    
    // Rozliczenie
    kwitujeOdbiorKwota: '',
    kwitujeOdbiorSlownie: '',
    kwitujeOdbiorData: '',
    kwitujeOdbiorPodpis: '',
    zalaczamDowodow: '',
    pobranoZaliczke: '',
    doWyplatyZwrotu: '',
    rachunekDataPrzedlozenia: '',
    rachunekPodpisPracownika: ''
  });

  // Aktualizuj dane pracownika gdy user się zmieni
  React.useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        pracownikImie: user.firstName || prev.pracownikImie || '',
        pracownikNazwisko: user.lastName || prev.pracownikNazwisko || '',
        stanowisko: user.position || prev.stanowisko || '',
        telefonKontaktowy: user.phone || prev.telefonKontaktowy || ''
      }));
    }
  }, [user]);

  // Oblicz liczbę dni delegacji
  useEffect(() => {
    if (formData.dataOd && formData.dataDo) {
      const start = new Date(formData.dataOd);
      const end = new Date(formData.dataDo);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      setFormData(prev => ({ ...prev, liczbaDni: diffDays.toString() }));
    }
  }, [formData.dataOd, formData.dataDo]);

  // Automatyczna konwersja kwot na słowa
  useEffect(() => {
    if (formData.zaliczkaKwota) {
      const kwota = parseFloat(formData.zaliczkaKwota);
      if (!isNaN(kwota) && kwota > 0) {
        setFormData(prev => ({ ...prev, zaliczkaSlownie: numberToWords(kwota) }));
      }
    }
  }, [formData.zaliczkaKwota]);

  useEffect(() => {
    if (formData.zaliczkaZatwierdzonaKwota) {
      const kwota = parseFloat(formData.zaliczkaZatwierdzonaKwota);
      if (!isNaN(kwota) && kwota > 0) {
        setFormData(prev => ({ ...prev, zaliczkaZatwierdzonaSlownie: numberToWords(kwota) }));
      }
    }
  }, [formData.zaliczkaZatwierdzonaKwota]);

  useEffect(() => {
    if (formData.zatwierdzenieKwota) {
      const kwota = parseFloat(formData.zatwierdzenieKwota);
      if (!isNaN(kwota) && kwota > 0) {
        setFormData(prev => ({ ...prev, zatwierdzenieSlownie: numberToWords(kwota) }));
      }
    }
  }, [formData.zatwierdzenieKwota]);

  useEffect(() => {
    if (formData.kwitujeOdbiorKwota) {
      const kwota = parseFloat(formData.kwitujeOdbiorKwota);
      if (!isNaN(kwota) && kwota > 0) {
        setFormData(prev => ({ ...prev, kwitujeOdbiorSlownie: numberToWords(kwota) }));
      }
    }
  }, [formData.kwitujeOdbiorKwota]);

  // Oblicz sumę kosztów
  useEffect(() => {
    const ryczalty = parseFloat(formData.kosztyRyczaltyDojazdy) || 0;
    const udokumentowane = parseFloat(formData.kosztyDojazdyUdokumentowane) || 0;
    const razemPrzejazdy = ryczalty + udokumentowane;
    
    const diety = parseFloat(formData.kosztyDiety) || 0;
    const noclegiRachunki = parseFloat(formData.kosztyNoclegiRachunki) || 0;
    const noclegiRyczalt = parseFloat(formData.kosztyNoclegiRyczalt) || 0;
    const inne = parseFloat(formData.kosztyInneWydatki) || 0;
    
    const ogolem = razemPrzejazdy + diety + noclegiRachunki + noclegiRyczalt + inne;
    
    setFormData(prev => ({
      ...prev,
      kosztyRazemPrzejazdy: razemPrzejazdy > 0 ? razemPrzejazdy.toString() : '',
      kosztyOgolem: ogolem > 0 ? ogolem.toString() : '',
      kosztyOgolemSlownie: ogolem > 0 ? numberToWords(ogolem) : ''
    }));
  }, [
    formData.kosztyRyczaltyDojazdy,
    formData.kosztyDojazdyUdokumentowane,
    formData.kosztyDiety,
    formData.kosztyNoclegiRachunki,
    formData.kosztyNoclegiRyczalt,
    formData.kosztyInneWydatki
  ]);

  // Handlery dla wyjazdów/przyjazdów
  const addWyjazdPrzyjazd = () => {
    setFormData(prev => ({
      ...prev,
      wyjazdyPrzyjazdy: [...prev.wyjazdyPrzyjazdy, {
        wyjazdMiejscowosc: '',
        wyjazdData: '',
        wyjazdGodzina: '',
        przyjazdMiejscowosc: '',
        przyjazdData: '',
        przyjazdGodzina: '',
        srodekLokomocji: '',
        kosztPrzejazdu: { zl: '', gr: '' }
      }]
    }));
  };

  const removeWyjazdPrzyjazd = (index) => {
    setFormData(prev => ({
      ...prev,
      wyjazdyPrzyjazdy: prev.wyjazdyPrzyjazdy.filter((_, i) => i !== index)
    }));
  };

  const updateWyjazdPrzyjazd = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.wyjazdyPrzyjazdy];
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updated[index] = {
          ...updated[index],
          [parent]: {
            ...updated[index][parent],
            [child]: value
          }
        };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return { ...prev, wyjazdyPrzyjazdy: updated };
    });
  };

  // Mutacja zapisywania/aktualizacji wniosku
  const saveWniosekMutation = useMutation(
    (wniosekData) => {
      if (id) {
        return kadryService.updateWniosekDelegacyjny(id, wniosekData);
      }
      return kadryService.createWniosekDelegacyjny(wniosekData);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('wnioski-delegacyjne');
        queryClient.invalidateQueries(['wniosek-delegacyjny', id]);
        toast.success(id ? 'Wniosek delegacyjny został zaktualizowany' : 'Wniosek delegacyjny został złożony pomyślnie');
        if (!id) {
          // Przekieruj do listy wniosków lub edycji nowego wniosku
        }
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 'Wystąpił błąd podczas zapisywania wniosku';
        toast.error(errorMessage);
      }
    }
  );

  const podpiszMutation = useMutation(
    () => kadryService.podpiszWniosekDelegacyjny(id, { podpisPrzełożonego }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('wnioski-delegacyjne');
        queryClient.invalidateQueries(['wniosek-delegacyjny', id]);
        setPodpisPrzełożonego('');
        toast.success('Polecenie wyjazdu zostało podpisane');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Nie udało się podpisać wniosku');
      }
    }
  );

  const sumaWplatyZwrotu = useMemo(
    () => sumaKosztowPrzejazduZListy(formData.wyjazdyPrzyjazdy),
    [formData.wyjazdyPrzyjazdy]
  );

  /** Ostatnia znana suma z tabeli „Koszty przejazdu” (do podpowiedzi „Zatwierdzono na sumę”) */
  const prevRachunkSumaDlaZatwierdzeniaRef = useRef(null);

  useEffect(() => {
    prevRachunkSumaDlaZatwierdzeniaRef.current = null;
  }, [id]);

  const wEarly = wniosekData?.wniosek;
  const jestHrKsEarly = user?.department === 'HR' || user?.department === 'KSIĘGOWOŚĆ';
  const merytorycznieOkEarly = !!(
    wEarly?.sprawdzenieMerytoryczne?.podpis &&
    String(wEarly.sprawdzenieMerytoryczne.podpis).trim() !== ''
  );
  // HR/Księgowość: zakładka akceptacji formalnej dopiero po sprawdzeniu merytorycznym przełożonego.
  const pokazZakladkeAkceptacjeEarly = !!(
    wEarly?.rachunekKosztowUzupełniony &&
    wEarly?.podpisPrzełożonego &&
    merytorycznieOkEarly
  );
  const mozeEdytowacAkceptacjeFormalneEarly =
    pokazZakladkeAkceptacjeEarly && !wniosekData?.wniosek?.akceptacjaFormalnaZakonczona &&
    (jestHrKsEarly ||
      wniosekData?.widokWniosku === 'hr_ks' ||
      user?.role === 'super admin' ||
      user?.role === 'admin');

  /**
   * HR/Księgowość: „Zatwierdzono na sumę” = suma z rachunku (tabela kosztów przejazdu),
   * z możliwością ręcznej zmiany. Puste pole uzupełniamy; jeśli kwota nadal = poprzedniej
   * sumie rachunku, aktualizujemy przy zmianie kosztów.
   */
  useEffect(() => {
    if (!mozeEdytowacAkceptacjeFormalneEarly || !wniosekData) return;
    const suma = sumaWplatyZwrotu;
    const prevR = prevRachunkSumaDlaZatwierdzeniaRef.current;

    setFormData((prev) => {
      const cur = prev.zatwierdzenieKwota;
      const curNum = cur === '' || cur == null ? NaN : parseFloat(String(cur).replace(',', '.'));
      const empty = cur === '' || cur == null;

      if (suma <= 0) {
        prevRachunkSumaDlaZatwierdzeniaRef.current = suma;
        return prev;
      }

      const nextStr = String(Math.round(suma * 100) / 100);

      if (empty) {
        prevRachunkSumaDlaZatwierdzeniaRef.current = suma;
        return { ...prev, zatwierdzenieKwota: nextStr };
      }

      if (prevR != null && !isNaN(curNum) && Math.abs(curNum - prevR) < 0.005) {
        prevRachunkSumaDlaZatwierdzeniaRef.current = suma;
        return { ...prev, zatwierdzenieKwota: nextStr };
      }

      prevRachunkSumaDlaZatwierdzeniaRef.current = suma;
      return prev;
    });
  }, [mozeEdytowacAkceptacjeFormalneEarly, sumaWplatyZwrotu, wniosekData]);

  const buildSubmitPayload = () => {
    const wyjazdyPrzyjazdy = formData.wyjazdyPrzyjazdy.map((wp) => ({
      ...wp,
      wyjazdData: wp.wyjazdData ? new Date(wp.wyjazdData) : undefined,
      przyjazdData: wp.przyjazdData ? new Date(wp.przyjazdData) : undefined,
      kosztPrzejazdu: {
        zl: parseFloat(wp.kosztPrzejazdu?.zl) || 0,
        gr: parseFloat(wp.kosztPrzejazdu?.gr) || 0
      }
    }));
    const rachunekSumaWplatyZwrotu = sumaKosztowPrzejazduZListy(formData.wyjazdyPrzyjazdy);

    return {
      ...formData,
      dataOd: new Date(formData.dataOd),
      dataDo: new Date(formData.dataDo),
      dataPolecenia: formData.dataPolecenia ? new Date(formData.dataPolecenia) : undefined,
      dataAkceptacjiPolecenia: formData.dataAkceptacjiPolecenia
        ? new Date(formData.dataAkceptacjiPolecenia)
        : undefined,
      podpisAkceptujacego: null,
      przełożony: formData.przełożony,
      samochodSluzbowyNumerRejestracji: formData.samochodSluzbowyNumerRejestracji
        ? String(formData.samochodSluzbowyNumerRejestracji).trim().toUpperCase()
        : undefined,
      srodekTransportu: (() => {
        const nr =
          formData.samochodSluzbowyNumerRejestracji &&
          String(formData.samochodSluzbowyNumerRejestracji).trim();
        return nr ? `Samochód służbowy, nr rej. ${nr.toUpperCase()}` : '';
      })(),
      zatwierdzenieData: formData.zatwierdzenieData ? new Date(formData.zatwierdzenieData) : undefined,
      kwitujeOdbiorData: formData.kwitujeOdbiorData ? new Date(formData.kwitujeOdbiorData) : undefined,
      wyjazdyPrzyjazdy,
      rachunekSumaWplatyZwrotu,
      rachunekDataPrzedlozenia: formData.rachunekDataPrzedlozenia
        ? new Date(formData.rachunekDataPrzedlozenia)
        : undefined,
      rachunekPodpisPracownika: formData.rachunekPodpisPracownika || undefined,
      sprawdzenieMerytoryczne: {
        data: formData.sprawdzenieMerytoryczne.data
          ? new Date(formData.sprawdzenieMerytoryczne.data)
          : undefined,
        podpis: formData.sprawdzenieMerytoryczne.podpis || undefined,
        imieNazwisko: formData.sprawdzenieMerytoryczne?.imieNazwisko || undefined
      },
      sprawdzenieFormalne: {
        data: formData.sprawdzenieFormalne.data ? new Date(formData.sprawdzenieFormalne.data) : undefined,
        podpis: formData.sprawdzenieFormalne.podpis || undefined,
        imieNazwisko: formData.sprawdzenieFormalne?.imieNazwisko || undefined
      }
    };
  };

  const potwierdzRachunekZapisMutation = useMutation(
    async () => {
      const payload = buildSubmitPayload();
      await kadryService.updateWniosekDelegacyjny(id, payload);
      return kadryService.potwierdzRachunekKosztowDelegacji(id);
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('wnioski-delegacyjne');
        queryClient.invalidateQueries(['wniosek-delegacyjny', id]);
        toast.success(data?.message || 'Zapisano rachunek i potwierdzono jego uzupełnienie');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Nie udało się zapisać lub potwierdzić rachunku');
      }
    }
  );

  const zwrotDoPracownikaMutation = useMutation(
    () => kadryService.zwrocDelegacjeDoPracownika(id, komentarzZwrotu),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('wnioski-delegacyjne');
        queryClient.invalidateQueries(['wniosek-delegacyjny', id]);
        setZwrotModalOpen(false);
        setKomentarzZwrotu('');
        toast.success(data?.message || 'Wniosek zwrócony do pracownika');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Nie udało się zwrócić wniosku do pracownika');
      }
    }
  );

  const zwrotPoleceniaMutation = useMutation(
    () => kadryService.zwrocPolecenieDelegacjiDoPracownika(id, komentarzZwrotu),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('wnioski-delegacyjne');
        queryClient.invalidateQueries(['wniosek-delegacyjny', id]);
        setZwrotPolecenieModalOpen(false);
        setKomentarzZwrotu('');
        toast.success(data?.message || 'Wniosek zwrócony do pracownika do poprawy');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Nie udało się zwrócić wniosku');
      }
    }
  );

  const usunDelegacjeMutation = useMutation(
    () => kadryService.usunDelegacje(id),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('wnioski-delegacyjne');
        toast.success(data?.message || 'Wniosek delegacyjny został usunięty');
        navigate('/kadry/wnioski-delegacyjne');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Nie udało się usunąć wniosku');
      }
    }
  );

  const saveAkceptacjeMutation = useMutation(
    (payload) => kadryService.updateWniosekDelegacyjny(id, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('wnioski-delegacyjne');
        queryClient.invalidateQueries(['wniosek-delegacyjny', id]);
        toast.success('Akceptacje zostały zapisane');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Nie udało się zapisać akceptacji');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.pracownikImie || !formData.pracownikNazwisko) {
      toast.error('Proszę wypełnić imię i nazwisko');
      return;
    }
    if (!formData.celDelegacji || !formData.miejsceDelegacji) {
      toast.error('Proszę wypełnić cel i miejsce delegacji');
      return;
    }
    if (!formData.dataOd || !formData.dataDo) {
      toast.error('Proszę wybrać okres delegacji');
      return;
    }
    if (!formData.przełożony) {
      toast.error('Wybierz przełożonego, do którego ma trafić wniosek');
      return;
    }

    saveWniosekMutation.mutate(buildSubmitPayload());
  };

  const handlePrint = () => {
    window.print();
  };

  const focusParam = searchParams.get('focus');

  /** Wejście z powiadomienia / listy (?focus=) — zakładka Akceptacje, scroll, migająca ramka */
  useEffect(() => {
    if (!id || isLoadingWniosek || !wniosekData?.wniosek || !focusParam) return;

    const wn = wniosekData.wniosek;
    const widok = wniosekData.widokWniosku;
    const pokazAkceptacje = !!(wn.rachunekKosztowUzupełniony && wn.podpisPrzełożonego);
    const merytPod =
      wn.sprawdzenieMerytoryczne?.podpis &&
      String(wn.sprawdzenieMerytoryczne.podpis).trim() !== '';
    const jestHrKs = user?.department === 'HR' || user?.department === 'KSIĘGOWOŚĆ';

    const stripFocus = () => {
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.delete('focus');
          return n;
        },
        { replace: true }
      );
    };

    const adresatMozeMerytorycznie =
      widok === 'adresat' && pokazAkceptacje && !wn.akceptacjaFormalnaZakonczona && !merytPod;

    if (focusParam === 'podpis-merytoryczny') {
      if (!pokazAkceptacje) {
        stripFocus();
        return;
      }
      setActiveSection('akceptacje');
      const t = window.setTimeout(() => {
        merytorycznyPodpisRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (adresatMozeMerytorycznie) setPodpisHighlight('merytoryczny');
        stripFocus();
      }, 160);
      return () => window.clearTimeout(t);
    }

    if (focusParam === 'podpis-formalny') {
      if (!pokazAkceptacje || !merytPod) {
        stripFocus();
        return;
      }
      const hrMozeFormal = widok === 'hr_ks' && jestHrKs && !wn.akceptacjaFormalnaZakonczona;
      setActiveSection('akceptacje');
      const t = window.setTimeout(() => {
        formalnyBlokRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (hrMozeFormal) setPodpisHighlight('formalny');
        stripFocus();
      }, 160);
      return () => window.clearTimeout(t);
    }

    stripFocus();
  }, [focusParam, id, isLoadingWniosek, wniosekData, setSearchParams, user?.department]);

  useEffect(() => {
    if (!podpisHighlight) return;
    const t = window.setTimeout(() => setPodpisHighlight(null), 14000);
    return () => window.clearTimeout(t);
  }, [podpisHighlight]);

  useEffect(() => {
    if (activeSection !== 'akceptacje') setPodpisHighlight(null);
  }, [activeSection]);

  if (isLoadingWniosek) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  const isAuthor = !id || wniosekData?.widokWniosku === 'autor';
  const w = wniosekData?.wniosek;
  const dokumentZablokowany = !!w?.akceptacjaFormalnaZakonczona;
  const mozeEdytowacEwidencje = isEditing && !dokumentZablokowany;
  const isAdresat = id && wniosekData?.widokWniosku === 'adresat';
  const isHrKsWidok = id && wniosekData?.widokWniosku === 'hr_ks';
  const jestHrKs = user?.department === 'HR' || user?.department === 'KSIĘGOWOŚĆ';
  const pokazBanerRachunek =
    isAuthor &&
    w?.podpisPrzełożonego &&
    (w?.oczekujeNaRachunekKosztow || w?.oczekujeNaPoprawkeRachunku) &&
    !w?.rachunekKosztowUzupełniony;
  const pokazBanerPoprawkaRachunku = !!(isAuthor && w?.oczekujeNaPoprawkeRachunku && w?.aktywnyKomentarzZwrotu?.komentarz);
  const pokazBanerPoprawkaPolecenia = !!(isAuthor && w?.oczekujeNaPoprawkePolecenia && w?.aktywnyKomentarzZwrotu?.komentarz);
  const mozePodpisacJakoPrzelozony = isAdresat && w && !w.podpisPrzełożonego && !w.oczekujeNaPoprawkePolecenia;

  const merytorycznieZatwierdzone = !!(
    w?.sprawdzenieMerytoryczne?.podpis &&
    String(w.sprawdzenieMerytoryczne.podpis).trim() !== ''
  );

  const etapPoczatkowyW = !!(
    w &&
    !dokumentZablokowany &&
    !(w.podpisPrzełożonego && String(w.podpisPrzełożonego).trim()) &&
    !w.rachunekKosztowUzupełniony
  );
  const przedRachunkiemW = !!(
    w &&
    !dokumentZablokowany &&
    w.podpisPrzełożonego &&
    String(w.podpisPrzełożonego).trim() !== '' &&
    !w.rachunekKosztowUzupełniony &&
    !merytorycznieZatwierdzone
  );
  const jestAdminem = user?.role === 'admin' || user?.role === 'super admin';
  const mozeUsunacTenWniosek =
    (w?.oczekujeNaPoprawkePolecenia && isAuthor) ||
    (etapPoczatkowyW && (isAuthor || isAdresat || jestAdminem || jestHrKs)) ||
    (przedRachunkiemW && (isAuthor || isAdresat || jestAdminem));
  const mozeZwrocPolecenieTenWniosek =
    etapPoczatkowyW && !w?.oczekujeNaPoprawkePolecenia && (isAdresat || jestAdminem);

  /** Podgląd daty i podpisu przy rachunku — także dla przełożonego po zapisie przez pracownika */
  const pokazPodpisRachunkuReadOnly =
    !!w?.rachunekPodpisPracownika &&
    (isAdresat || (isAuthor && (w?.rachunekKosztowUzupełniony || !mozeEdytowacEwidencje)));

  /** Czy przełożony już podpisał sprawdzenie merytoryczne — wtedy wniosek może iść do HR/Księgowości. */
  /** Zakładka Akceptacje — po uzupełnieniu rachunku przez pracownika (dla przełożonego i podglądu autora) */
  const pokazZakladkeAkceptacje = !!(w?.rachunekKosztowUzupełniony && w?.podpisPrzełożonego);
  /** HR/Księgowość widzi zakładkę dopiero po sprawdzeniu merytorycznym przez przełożonego. */
  const pokazZakladkeAkceptacjeDlaHrKs = pokazZakladkeAkceptacje && merytorycznieZatwierdzone;
  /** Merytoryczne: tylko przełożony (adresat). Formalne i zatwierdzenie: tylko HR / Księgowość — i dopiero po merytorycznym. */
  const mozeEdytowacAkceptacjeMerytorycznie =
    isAdresat && pokazZakladkeAkceptacje && !dokumentZablokowany;
  const mozeEdytowacAkceptacjeFormalne =
    pokazZakladkeAkceptacjeDlaHrKs &&
    !dokumentZablokowany &&
    (jestHrKs || isHrKsWidok || user?.role === 'super admin' || user?.role === 'admin');
  const mozeZapisacAkceptacje =
    mozeEdytowacAkceptacjeMerytorycznie || mozeEdytowacAkceptacjeFormalne;
  const mozeZwrocDoPracownika =
    pokazZakladkeAkceptacjeDlaHrKs &&
    !dokumentZablokowany &&
    !!w?.rachunekKosztowUzupełniony &&
    !w?.oczekujeNaPoprawkeRachunku &&
    (jestHrKs || isHrKsWidok || user?.role === 'super admin' || user?.role === 'admin');

  const podpisAkceptujacegoSrc =
    formData.zatwierdzeniePodpisy || w?.zatwierdzeniePodpisy || '';
  const nazwaPodPodpisemAkceptujacego = formatSignerNameUnderPad(
    podpisAkceptujacegoSrc,
    (
      formData.zatwierdzenieImieNazwiskoAkceptujacego ||
      w?.zatwierdzenieImieNazwiskoAkceptujacego ||
      ''
    ).trim(),
    undefined
  );

  const srodekTransportuDoDruku =
    (w && w.srodekTransportu) ||
    (formData.samochodSluzbowyNumerRejestracji
      ? `Samochód służbowy, nr rej. ${String(formData.samochodSluzbowyNumerRejestracji).trim().toUpperCase()}`
      : '') ||
    '—';

  const nazwaPrzełożonegoDruk =
    w?.przełożony && typeof w.przełożony === 'object'
      ? [w.przełożony.firstName, w.przełożony.lastName].filter(Boolean).join(' ').trim() ||
        w.przełożony.username ||
        ''
      : '';

  const podpisPrzełożonegoSrcDruk = w?.podpisPrzełożonego || podpisPrzełożonego || '';
  const listaWyjazdowDruk = (() => {
    const raw = formData.wyjazdyPrzyjazdy || [];
    const out = raw.length ? [...raw] : [];
    const minRows = 4; /* wydruk: zawsze min. 4 wiersze (puste OK); więcej wpisów = więcej wierszy */
    while (out.length < minRows) out.push(null);
    return out;
  })();

  const handleSaveAkceptacje = (e) => {
    e.preventDefault();
    const payload = {};
    if (mozeEdytowacAkceptacjeMerytorycznie) {
      const podpisM = formData.sprawdzenieMerytoryczne?.podpis || undefined;
      payload.sprawdzenieMerytoryczne = {
        data: formData.sprawdzenieMerytoryczne?.data
          ? new Date(formData.sprawdzenieMerytoryczne.data)
          : undefined,
        podpis: podpisM,
        imieNazwisko: podpisM
          ? formatUserFullName(user) || formData.sprawdzenieMerytoryczne?.imieNazwisko || undefined
          : undefined
      };
    }
    if (mozeEdytowacAkceptacjeFormalne) {
      const podpisF = formData.sprawdzenieFormalne?.podpis || undefined;
      payload.sprawdzenieFormalne = {
        data: formData.sprawdzenieFormalne?.data
          ? new Date(formData.sprawdzenieFormalne.data)
          : undefined,
        podpis: podpisF,
        imieNazwisko: podpisF
          ? formatUserFullName(user) || formData.sprawdzenieFormalne?.imieNazwisko || undefined
          : undefined
      };
      payload.zatwierdzenieKwota =
        formData.zatwierdzenieKwota !== '' && formData.zatwierdzenieKwota != null
          ? parseFloat(formData.zatwierdzenieKwota)
          : undefined;
      payload.zatwierdzenieSlownie = formData.zatwierdzenieSlownie
        ? String(formData.zatwierdzenieSlownie).trim()
        : undefined;
      payload.zatwierdzenieData = formData.zatwierdzenieData
        ? new Date(formData.zatwierdzenieData)
        : undefined;
      payload.zatwierdzeniePodpisy = formData.zatwierdzeniePodpisy || undefined;
      const podpisAkcept = formData.zatwierdzeniePodpisy || undefined;
      payload.zatwierdzenieImieNazwiskoAkceptujacego = podpisAkcept
        ? formatUserFullName(user) || formData.zatwierdzenieImieNazwiskoAkceptujacego || undefined
        : undefined;
    }
    saveAkceptacjeMutation.mutate(payload);
  };

  return (
    <>
      <div className="space-y-6 print:hidden">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6 print:hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/kadry/dokumenty"
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Powrót do dokumentów"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </Link>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  {!id
                    ? 'Nowy wniosek delegacyjny'
                    : isAuthor
                      ? 'Edycja wniosku delegacyjnego'
                      : 'Wniosek delegacyjny (podgląd)'}
                </h1>
                {dokumentZablokowany && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-violet-300 bg-violet-100 px-2.5 py-0.5 text-xs font-semibold text-violet-900">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                    Zaakceptowany — zamknięty
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-500">
                {!id
                  ? 'Wypełnij formularz wniosku delegacyjnego'
                  : isAuthor
                    ? 'Edytuj wniosek delegacyjny'
                    : isHrKsWidok
                      ? 'Akceptacja formalna i rachunkowa — uzupełnij sekcję w zakładce Akceptacje (po uzupełnieniu rachunku przez pracownika).'
                      : mozePodpisacJakoPrzelozony
                        ? 'Podpisz polecenie wyjazdu poniżej — pracownik otrzyma powiadomienie i e-mail'
                        : w?.podpisPrzełożonego
                          ? 'Polecenie zostało przez Ciebie podpisane'
                          : 'Wniosek skierowany do Ciebie jako przełożonego'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {id && isAuthor && !dokumentZablokowany && (
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
              >
                <Edit className="h-4 w-4" />
                <span>{isEditing ? 'Anuluj edycję' : 'Edytuj'}</span>
              </button>
            )}
            <button
              onClick={handlePrint}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
            >
              <Printer className="h-4 w-4" />
              <span>Drukuj</span>
            </button>
            {mozeZwrocPolecenieTenWniosek && (
              <button
                type="button"
                onClick={() => { setZwrotPolecenieModalOpen(true); setKomentarzZwrotu(''); }}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center space-x-2 text-sm"
              >
                <RotateCcw className="h-4 w-4" />
                <span>Zwróć do poprawy</span>
              </button>
            )}
            {mozeUsunacTenWniosek && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Czy na pewno usunąć ten wniosek delegacyjny? Tej operacji nie można cofnąć.')) {
                    usunDelegacjeMutation.mutate();
                  }
                }}
                disabled={usunDelegacjeMutation.isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2 text-sm"
              >
                <Trash2 className="h-4 w-4" />
                <span>Usuń wniosek</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {dokumentZablokowany && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900 shadow-sm print:hidden">
          <p className="font-medium">Wniosek został zaakceptowany formalnie</p>
          <p className="mt-1 text-sm text-emerald-800">
            Dokument jest zablokowany do edycji. Pracownik otrzymał powiadomienie oraz e-mail z kwotą zatwierdzoną do
            wypłaty.
          </p>
        </div>
      )}

      {pokazBanerPoprawkaPolecenia && (
        <div className="rounded-xl border border-orange-300 bg-orange-50 p-4 shadow-sm print:hidden">
          <div className="flex gap-3">
            <AlertTriangle className="h-6 w-6 shrink-0 text-orange-700" aria-hidden />
            <div>
              <p className="font-semibold text-orange-950">Wniosek zwrócony do poprawy (etap początkowy)</p>
              <p className="mt-1 text-sm text-orange-900/90">
                {w.aktywnyKomentarzZwrotu.zwrocilImieNazwisko || 'Przełożony'} (
                {w.aktywnyKomentarzZwrotu.data
                  ? new Date(w.aktywnyKomentarzZwrotu.data).toLocaleString('pl-PL')
                  : '—'}
                ) przekazał(a) uwagi:
              </p>
              <p className="mt-2 rounded-lg border border-orange-200 bg-white p-3 text-sm text-gray-900 whitespace-pre-wrap">
                {w.aktywnyKomentarzZwrotu.komentarz}
              </p>
              <p className="mt-2 text-sm text-orange-900/90">
                Popraw dane wniosku i kliknij <strong>Zaktualizuj wniosek</strong> — wniosek trafi ponownie do przełożonego do podpisania polecenia wyjazdu.
              </p>
            </div>
          </div>
        </div>
      )}

      {pokazBanerPoprawkaRachunku && (
        <div className="rounded-xl border border-orange-300 bg-orange-50 p-4 shadow-sm print:hidden">
          <div className="flex gap-3">
            <AlertTriangle className="h-6 w-6 shrink-0 text-orange-700" aria-hidden />
            <div>
              <p className="font-semibold text-orange-950">Rachunek zwrócony do poprawy</p>
              <p className="mt-1 text-sm text-orange-900/90">
                {w.aktywnyKomentarzZwrotu.zwrocilImieNazwisko || 'HR / Księgowość'} (
                {w.aktywnyKomentarzZwrotu.data
                  ? new Date(w.aktywnyKomentarzZwrotu.data).toLocaleString('pl-PL')
                  : '—'}
                ) przekazał(a) uwagi:
              </p>
              <p className="mt-2 rounded-lg border border-orange-200 bg-white p-3 text-sm text-gray-900 whitespace-pre-wrap">
                {w.aktywnyKomentarzZwrotu.komentarz}
              </p>
              <p className="mt-2 text-sm text-orange-900/90">
                Popraw rachunek w zakładce <strong>Rachunek kosztów podróży</strong> i ponownie potwierdź uzupełnienie.
                Po poprawce wniosek trafi <strong>bezpośrednio do HR/Księgowości</strong> (bez ponownego podpisu
                merytorycznego przełożonego).
              </p>
            </div>
          </div>
        </div>
      )}

      {pokazBanerRachunek && !pokazBanerPoprawkaRachunku && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm print:hidden">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex gap-3">
              <ClipboardCheck className="h-6 w-6 shrink-0 text-amber-700" aria-hidden />
              <div>
                <p className="font-semibold text-amber-900">Polecenie podpisane przez przełożonego</p>
                <p className="mt-1 text-sm text-amber-900/90">
                  Po powrocie z delegacji przejdź do zakładki <strong>Rachunek kosztów podróży</strong>: uzupełnij
                  tabelę kosztów przejazdu, wpisz datę złożenia rachunku i złóż <strong>podpis pracownika przy
                  rachunku</strong>. Dopiero wtedy pojawi się przycisk potwierdzenia uzupełnienia rachunku (na dole
                  tej zakładki). Możesz też wcześniej zapisać wniosek przyciskiem „Zaktualizuj wniosek”.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setActiveSection('rachunek')}
                className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
              >
                Przejdź do rachunku
              </button>
            </div>
          </div>
        </div>
      )}

      {isAdresat && w?.podpisPrzełożonego && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 print:hidden">
          Data podpisu:{' '}
          {w.dataPodpisuPrzełożonego
            ? new Date(w.dataPodpisuPrzełożonego).toLocaleString('pl-PL')
            : '—'}
        </div>
      )}

      {mozePodpisacJakoPrzelozony && (
        <div className="rounded-xl border border-blue-200 bg-white p-6 shadow print:hidden">
          <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold text-gray-900">
            <PenLine className="h-5 w-5 text-blue-600" />
            Podpis przełożonego — polecenie wyjazdu
          </h2>
          <p className="mb-4 text-sm text-gray-600">
            Złóż podpis elektroniczny, aby zatwierdzić polecenie. Pracownik otrzyma powiadomienie w systemie oraz
            wiadomość e-mail z informacją o podpisie i przypomnieniem o rachunku kosztów po powrocie.
          </p>
          <SignaturePad
            onSignatureChange={setPodpisPrzełożonego}
            width={400}
            height={150}
            signature={podpisPrzełożonego || undefined}
            signerName={formatSignerNameUnderPad(podpisPrzełożonego, w?.podpisPrzełożonegoImieNazwisko, user)}
          />
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={!podpisPrzełożonego || podpiszMutation.isLoading}
              onClick={() => podpiszMutation.mutate()}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {podpiszMutation.isLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  Podpisywanie…
                </>
              ) : (
                <>
                  <PenLine className="h-4 w-4" />
                  Podpisz polecenie
                </>
              )}
            </button>
            {!podpisPrzełożonego && (
              <span className="text-xs text-gray-500">Narysuj podpis na polu powyżej.</span>
            )}
          </div>
        </div>
      )}

      {/* Zakładki: polecenie | rachunek (tabela) */}
      <div className="bg-white shadow rounded-lg print:hidden">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              type="button"
              onClick={() => setActiveSection('polecenie')}
              className={`${
                activeSection === 'polecenie'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } py-4 px-6 text-sm font-medium border-b-2 transition-colors`}
            >
              Polecenie wyjazdu służbowego
            </button>
            <button
              type="button"
              onClick={() => setActiveSection('rachunek')}
              className={`${
                activeSection === 'rachunek'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } py-4 px-6 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-2`}
            >
              Rachunek kosztów podróży
              {pokazBanerRachunek && (
                <span
                  className="inline-flex h-2 w-2 rounded-full bg-amber-500"
                  title="Wymaga uzupełnienia po powrocie"
                />
              )}
            </button>
            {pokazZakladkeAkceptacje && (
              <button
                type="button"
                onClick={() => setActiveSection('akceptacje')}
                className={`${
                  activeSection === 'akceptacje'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-red-600 hover:text-red-700 hover:border-red-200'
                } py-4 px-6 text-sm font-semibold border-b-2 transition-colors tracking-wide`}
              >
                Akceptacje
              </button>
            )}
          </nav>
        </div>
      </div>

      {activeSection === 'polecenie' && (
      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-8 print:shadow-none">
        <div className="mb-8 max-w-3xl mx-auto">
            <h2 className="text-center text-xl font-bold text-gray-900 mb-10 tracking-tight">
              Polecenie wyjazdu służbowego
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-6">
              <div className="flex flex-wrap items-end gap-2">
                <span className="text-sm text-gray-900 shrink-0 font-medium">Z dnia:</span>
                <input
                  type="date"
                  value={formData.dataPolecenia}
                  onChange={(e) => setFormData({ ...formData, dataPolecenia: e.target.value })}
                  disabled={!mozeEdytowacEwidencje}
                  className="flex-1 min-w-[10rem] px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-700"
                />
              </div>
              <div className="flex flex-wrap items-end gap-2 sm:justify-end">
                <span className="text-sm text-gray-900 shrink-0 font-medium">Dla:</span>
                <input
                  type="text"
                  value={`${formData.pracownikImie} ${formData.pracownikNazwisko}`.trim()}
                  onChange={(e) => {
                    const parts = e.target.value.trim().split(/\s+/);
                    setFormData({
                      ...formData,
                      pracownikImie: parts[0] || '',
                      pracownikNazwisko: parts.slice(1).join(' ') || ''
                    });
                  }}
                  disabled={!mozeEdytowacEwidencje}
                  className="flex-1 min-w-[12rem] px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-700"
                  placeholder="imię i nazwisko"
                />
              </div>
            </div>

            <div className="mb-6 flex flex-wrap items-end gap-2">
              <span className="text-sm text-gray-900 shrink-0 font-medium">Stanowisko:</span>
              <input
                type="text"
                value={formData.stanowisko}
                onChange={(e) => setFormData({ ...formData, stanowisko: e.target.value })}
                disabled={!mozeEdytowacEwidencje}
                  className="flex-1 min-w-[12rem] px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-700"
                  placeholder="—"
              />
            </div>

            <div className="mb-6 flex flex-wrap items-end gap-2">
              <span className="text-sm text-gray-900 shrink-0 font-medium">Do:</span>
              <input
                type="text"
                value={formData.miejsceDelegacji}
                onChange={(e) => setFormData({ ...formData, miejsceDelegacji: e.target.value })}
                disabled={!mozeEdytowacEwidencje}
                className="flex-1 min-w-[12rem] px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-700"
                placeholder="miejscowość / adres wyjazdu"
              />
            </div>

            <div className="mb-6 flex flex-wrap items-end gap-x-2 gap-y-2">
              <span className="text-sm text-gray-900 shrink-0 font-medium">Na czas od</span>
              <input
                type="date"
                value={formData.dataOd}
                onChange={(e) => setFormData({ ...formData, dataOd: e.target.value })}
                disabled={!mozeEdytowacEwidencje}
                className="w-[10.5rem] px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-700"
              />
              <span className="text-sm text-gray-900 font-medium">do</span>
              <input
                type="date"
                value={formData.dataDo}
                onChange={(e) => setFormData({ ...formData, dataDo: e.target.value })}
                disabled={!mozeEdytowacEwidencje}
                className="w-[10.5rem] px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-700"
              />
            </div>

            <div className="mb-6 flex flex-wrap items-start gap-2">
              <span className="text-sm text-gray-900 shrink-0 font-medium pt-2">W celu:</span>
              <textarea
                value={formData.celDelegacji}
                onChange={(e) => setFormData({ ...formData, celDelegacji: e.target.value })}
                disabled={!mozeEdytowacEwidencje}
                rows={3}
                className="flex-1 min-w-[12rem] px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-700 resize-y min-h-[4.5rem]"
                placeholder="cel wyjazdu służbowego"
              />
            </div>

            <div className="mb-10 flex flex-wrap items-end gap-x-2 gap-y-2">
              <span className="text-sm text-gray-900 font-medium">
                Środki lokomocji: Samochód służbowy nr. rejestracji
              </span>
              <input
                type="text"
                value={formData.samochodSluzbowyNumerRejestracji}
                onChange={(e) => setFormData({ ...formData, samochodSluzbowyNumerRejestracji: e.target.value.toUpperCase() })}
                disabled={!mozeEdytowacEwidencje}
                maxLength={20}
                className="w-36 sm:w-44 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm uppercase tracking-wide focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-700"
                placeholder="np. WW 12345"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 mt-12">
              <div className="group relative overflow-visible rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-gray-50/80 p-6 shadow-sm ring-1 ring-black/5 transition-shadow hover:shadow-md">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Data</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                  <input
                    type="date"
                    value={formData.dataAkceptacjiPolecenia}
                    onChange={(e) => setFormData({ ...formData, dataAkceptacjiPolecenia: e.target.value })}
                    disabled={!mozeEdytowacEwidencje}
                    className="w-full pl-11 pr-4 py-3 rounded-xl border-0 bg-white/80 text-gray-900 text-sm font-medium shadow-sm ring-1 ring-gray-200/80 focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 focus:ring-offset-transparent disabled:bg-gray-100 disabled:text-gray-500 transition-all"
                  />
                </div>
              </div>
              <div className="group relative overflow-visible rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-gray-50/80 p-6 shadow-sm ring-1 ring-black/5 transition-shadow hover:shadow-md">
                <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
                  <User className="h-4 w-4 text-gray-400" aria-hidden />
                  Wyślij wniosek do (przełożony) *
                </label>
                {mozeEdytowacEwidencje ? (
                  usersLoading ? (
                    <div className="flex items-center gap-2 py-4 text-sm text-gray-600">
                      <LoadingSpinner size="sm" />
                      Ładowanie listy…
                    </div>
                  ) : (
                    <SearchableUserSelect
                      users={usersData?.users || []}
                      value={formData.przełożony}
                      onChange={(id) => setFormData({ ...formData, przełożony: id })}
                      placeholder="— Wybierz przełożonego —"
                    />
                  )
                ) : (
                  <p className="text-sm font-medium text-gray-900 py-2">
                    {(() => {
                      const p = wniosekData?.wniosek?.przełożony;
                      if (!p) return '—';
                      if (typeof p === 'object') {
                        const name = `${p.firstName || ''} ${p.lastName || ''}`.trim();
                        return name || p.username || p.email || '—';
                      }
                      return '—';
                    })()}
                  </p>
                )}
                <p className="mt-3 text-xs text-gray-500 leading-relaxed">
                  Pracownik wskazuje adresata; wybrana osoba otrzyma powiadomienie w systemie o nowym wniosku.
                </p>
              </div>
            </div>

            {id && w?.podpisPrzełożonego && (
              <div className="mt-8 rounded-xl border border-emerald-100 bg-emerald-50/80 p-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800 mb-3">
                  Podpis przełożonego (polecenie wyjazdu)
                </p>
                <Podpis
                  value={w.podpisPrzełożonego}
                  alt="Podpis przełożonego"
                  className="max-h-32 max-w-md object-contain"
                />
                {w.podpisPrzełożonegoImieNazwisko && (
                  <p className="mt-2 text-sm font-medium text-gray-900">{w.podpisPrzełożonegoImieNazwisko}</p>
                )}
                {w.dataPodpisuPrzełożonego && (
                  <p className="mt-2 text-xs text-emerald-800/80">
                    {new Date(w.dataPodpisuPrzełożonego).toLocaleString('pl-PL')}
                  </p>
                )}
              </div>
            )}
          </div>

        {mozeEdytowacEwidencje && (
          <div className="mt-10 flex justify-end gap-3 print:hidden max-w-3xl mx-auto">
            <Link
              to="/kadry/dokumenty"
              className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 font-medium text-sm transition-colors"
            >
              Anuluj
            </Link>
            <button
              type="submit"
              disabled={saveWniosekMutation.isLoading || !formData.przełożony || usersLoading}
              className="px-6 py-2.5 rounded-xl bg-emerald-600 text-white font-medium text-sm shadow-sm hover:bg-emerald-700 hover:shadow disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
            >
              {saveWniosekMutation.isLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Zapisywanie...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>{id ? 'Zaktualizuj wniosek' : 'Złóż wniosek'}</span>
                </>
              )}
            </button>
          </div>
        )}
      </form>
      )}

      {activeSection === 'rachunek' && (
      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-8 print:shadow-none">
          <div className="max-w-5xl mx-auto w-full">
            <div className="mb-6 overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    {mozeEdytowacEwidencje && <th className="border border-gray-300 p-2 text-xs w-12"></th>}
                    <th colSpan="3" className="border border-gray-300 p-2 text-sm font-bold">
                      WYJAZD
                    </th>
                    <th colSpan="3" className="border border-gray-300 p-2 text-sm font-bold">
                      PRZYJAZD
                    </th>
                    <th rowSpan="2" className="border border-gray-300 p-2 text-sm font-bold">
                      Środki lokomocji*)
                    </th>
                    <th colSpan="2" className="border border-gray-300 p-2 text-sm font-bold">
                      Koszty przejazdu
                    </th>
                  </tr>
                  <tr className="bg-gray-100">
                    {mozeEdytowacEwidencje && <th className="border border-gray-300 p-2 text-xs"></th>}
                    <th className="border border-gray-300 p-2 text-xs">miejscowość</th>
                    <th className="border border-gray-300 p-2 text-xs">data</th>
                    <th className="border border-gray-300 p-2 text-xs">godz.</th>
                    <th className="border border-gray-300 p-2 text-xs">miejscowość</th>
                    <th className="border border-gray-300 p-2 text-xs">data</th>
                    <th className="border border-gray-300 p-2 text-xs">godz.</th>
                    <th className="border border-gray-300 p-2 text-xs">zł</th>
                    <th className="border border-gray-300 p-2 text-xs">gr</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.wyjazdyPrzyjazdy.length === 0 ? (
                    <tr>
                      <td colSpan={mozeEdytowacEwidencje ? "10" : "9"} className="border border-gray-300 p-4 text-center text-gray-500">
                        Brak wpisów. Kliknij "Dodaj wyjazd/przyjazd" aby dodać.
                      </td>
                    </tr>
                  ) : (
                    formData.wyjazdyPrzyjazdy.map((wp, index) => (
                      <tr key={index}>
                        {mozeEdytowacEwidencje && (
                          <td className="border border-gray-300 p-2">
                            <button
                              type="button"
                              onClick={() => removeWyjazdPrzyjazd(index)}
                              className="text-red-600 hover:text-red-800"
                              title="Usuń wiersz"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        )}
                        <td className="border border-gray-300 p-2">
                          <input
                            type="text"
                            value={wp.wyjazdMiejscowosc || ''}
                            onChange={(e) => updateWyjazdPrzyjazd(index, 'wyjazdMiejscowosc', e.target.value)}
                            disabled={!mozeEdytowacEwidencje}
                            className="w-full px-2 py-1 border-none focus:outline-none disabled:bg-transparent text-sm"
                            placeholder="..."
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="date"
                            value={wp.wyjazdData ? new Date(wp.wyjazdData).toISOString().split('T')[0] : ''}
                            onChange={(e) => updateWyjazdPrzyjazd(index, 'wyjazdData', e.target.value)}
                            disabled={!mozeEdytowacEwidencje}
                            className="w-full px-2 py-1 border-none focus:outline-none disabled:bg-transparent text-sm"
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="time"
                            value={wp.wyjazdGodzina || ''}
                            onChange={(e) => updateWyjazdPrzyjazd(index, 'wyjazdGodzina', e.target.value)}
                            disabled={!mozeEdytowacEwidencje}
                            className="w-full px-2 py-1 border-none focus:outline-none disabled:bg-transparent text-sm"
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="text"
                            value={wp.przyjazdMiejscowosc || ''}
                            onChange={(e) => updateWyjazdPrzyjazd(index, 'przyjazdMiejscowosc', e.target.value)}
                            disabled={!mozeEdytowacEwidencje}
                            className="w-full px-2 py-1 border-none focus:outline-none disabled:bg-transparent text-sm"
                            placeholder="..."
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="date"
                            value={wp.przyjazdData ? new Date(wp.przyjazdData).toISOString().split('T')[0] : ''}
                            onChange={(e) => updateWyjazdPrzyjazd(index, 'przyjazdData', e.target.value)}
                            disabled={!mozeEdytowacEwidencje}
                            className="w-full px-2 py-1 border-none focus:outline-none disabled:bg-transparent text-sm"
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="time"
                            value={wp.przyjazdGodzina || ''}
                            onChange={(e) => updateWyjazdPrzyjazd(index, 'przyjazdGodzina', e.target.value)}
                            disabled={!mozeEdytowacEwidencje}
                            className="w-full px-2 py-1 border-none focus:outline-none disabled:bg-transparent text-sm"
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="text"
                            value={wp.srodekLokomocji || ''}
                            onChange={(e) => updateWyjazdPrzyjazd(index, 'srodekLokomocji', e.target.value)}
                            disabled={!mozeEdytowacEwidencje}
                            className="w-full px-2 py-1 border-none focus:outline-none disabled:bg-transparent text-sm"
                            placeholder="..."
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="number"
                            step="0.01"
                            value={wp.kosztPrzejazdu?.zl || ''}
                            onChange={(e) => updateWyjazdPrzyjazd(index, 'kosztPrzejazdu.zl', e.target.value)}
                            disabled={!mozeEdytowacEwidencje}
                            className="w-full px-2 py-1 border-none focus:outline-none disabled:bg-transparent text-sm"
                            placeholder="0"
                          />
                        </td>
                        <td className="border border-gray-300 p-2">
                          <input
                            type="number"
                            step="0.01"
                            value={wp.kosztPrzejazdu?.gr || ''}
                            onChange={(e) => updateWyjazdPrzyjazd(index, 'kosztPrzejazdu.gr', e.target.value)}
                            disabled={!mozeEdytowacEwidencje}
                            className="w-full px-2 py-1 border-none focus:outline-none disabled:bg-transparent text-sm"
                            placeholder="0"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                  {/* Dodaj więcej wierszy do 10 */}
                  {Array.from({ length: Math.max(0, 10 - formData.wyjazdyPrzyjazdy.length) }).map((_, i) => (
                    <tr key={`empty-${i}`}>
                      <td colSpan={mozeEdytowacEwidencje ? "10" : "9"} className="border border-gray-300 p-2 h-10"></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {mozeEdytowacEwidencje && (
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={addWyjazdPrzyjazd}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2 text-sm"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Dodaj wyjazd/przyjazd</span>
                  </button>
                </div>
              )}
            </div>

            <div className="mt-8 rounded-lg border border-gray-200 bg-gray-50/80 p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-200 pb-4">
                <span className="text-sm font-semibold text-gray-900">
                  Suma ogółem do wpłaty/zwrotu:
                </span>
                <span className="text-lg font-bold tabular-nums text-gray-900">
                  {formatujSumeWplatyZwrotu(sumaWplatyZwrotu)}
                </span>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Kwota wyliczana automatycznie z kolumn „Koszty przejazdu” (zł i gr) w powyższej tabeli.
              </p>

              {pokazPodpisRachunkuReadOnly ? (
                <div className="mt-6 space-y-4">
                  {isAdresat && (
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Rachunek uzupełniony przez pracownika
                    </p>
                  )}
                  <p className="text-sm text-gray-800">
                    <span className="font-medium">Niniejszym rachunek przedkładam:</span>{' '}
                    {w?.rachunekDataPrzedlozenia
                      ? new Date(w.rachunekDataPrzedlozenia).toLocaleDateString('pl-PL')
                      : formData.rachunekDataPrzedlozenia
                        ? new Date(formData.rachunekDataPrzedlozenia).toLocaleDateString('pl-PL')
                        : '—'}
                  </p>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Podpis pracownika (rachunek kosztów)</p>
                    <img
                      src={w?.rachunekPodpisPracownika || formData.rachunekPodpisPracownika}
                      alt="Podpis pracownika przy rachunku"
                      className="max-h-28 max-w-md rounded border border-gray-200 bg-white object-contain p-2"
                    />
                    {(w?.rachunekPodpisPracownikaImieNazwisko ||
                      [formData.pracownikImie, formData.pracownikNazwisko].filter(Boolean).join(' ').trim()) && (
                      <p className="mt-2 text-sm font-medium text-gray-900">
                        {w?.rachunekPodpisPracownikaImieNazwisko ||
                          [formData.pracownikImie, formData.pracownikNazwisko].filter(Boolean).join(' ').trim()}
                      </p>
                    )}
                  </div>
                  {isAdresat && w?.rachunekKosztowUzupełniony && (
                    <p className="text-xs text-gray-500">
                      Pracownik potwierdził uzupełnienie rachunku kosztów podróży.
                    </p>
                  )}
                </div>
              ) : isAuthor && pokazBanerRachunek && mozeEdytowacEwidencje && !w?.rachunekKosztowUzupełniony ? (
                <div className="mt-6 space-y-4 print:hidden">
                  <p className="text-sm text-gray-800">
                    Niniejszym rachunek przedkładam:{' '}
                    <input
                      type="date"
                      value={formData.rachunekDataPrzedlozenia}
                      onChange={(e) =>
                        setFormData({ ...formData, rachunekDataPrzedlozenia: e.target.value })
                      }
                      className="ml-1 rounded-md border border-gray-300 px-2 py-1 text-sm"
                    />
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Podpis pracownika (rachunek kosztów) *
                    </label>
                    <SignaturePad
                      onSignatureChange={(sig) =>
                        setFormData({ ...formData, rachunekPodpisPracownika: sig })
                      }
                      width={400}
                      height={150}
                      signature={formData.rachunekPodpisPracownika || undefined}
                      signerName={formatSignerNameUnderPad(
                        formData.rachunekPodpisPracownika,
                        w?.rachunekPodpisPracownikaImieNazwisko,
                        user
                      )}
                    />
                    {formData.rachunekPodpisPracownika && (
                      <p className="mt-2 text-xs text-green-600">Podpis dodany</p>
                    )}
                  </div>
                  {pokazBanerRachunek &&
                    formData.rachunekDataPrzedlozenia &&
                    formData.rachunekPodpisPracownika &&
                    formData.wyjazdyPrzyjazdy?.length > 0 && (
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                        <p className="text-sm font-medium text-emerald-900 mb-3">
                          {w?.oczekujeNaPoprawkeRachunku || w?.zwrotDoHrPoPoprawce
                            ? 'Potwierdzam poprawiony rachunek kosztów podróży'
                            : 'Potwierdzam uzupełnienie rachunku kosztów podróży'}
                        </p>
                        <p className="text-xs text-emerald-800/90 mb-3">
                          {w?.zwrotDoHrPoPoprawce
                            ? 'Zapisze poprawiony rachunek i prześle go bezpośrednio do HR/Księgowości do akceptacji formalnej.'
                            : 'Zapisze wniosek z tabelą, datą i podpisem oraz oznaczy rachunek jako uzupełniony.'}
                        </p>
                        <button
                          type="button"
                          disabled={potwierdzRachunekZapisMutation.isLoading}
                          onClick={() => potwierdzRachunekZapisMutation.mutate()}
                          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {potwierdzRachunekZapisMutation.isLoading ? (
                            <>
                              <LoadingSpinner size="sm" />
                              Zapisywanie…
                            </>
                          ) : (
                            <>
                              <ClipboardCheck className="h-4 w-4" />
                              Potwierdzam uzupełnienie rachunku
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  {pokazBanerRachunek &&
                    (!formData.rachunekDataPrzedlozenia ||
                      !formData.rachunekPodpisPracownika ||
                      !formData.wyjazdyPrzyjazdy?.length) && (
                      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-md p-3">
                        Aby potwierdzić rachunek: dodaj co najmniej jeden wiersz w tabeli, uzupełnij datę złożenia i
                        podpis przy rachunku.
                      </p>
                    )}
                </div>
              ) : null}
            </div>
          </div>

        {mozeEdytowacEwidencje && (
          <div className="mt-8 flex justify-end gap-3 print:hidden max-w-5xl mx-auto">
            <Link
              to="/kadry/dokumenty"
              className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 font-medium text-sm transition-colors"
            >
              Anuluj
            </Link>
            <button
              type="submit"
              disabled={saveWniosekMutation.isLoading || !formData.przełożony || usersLoading}
              className="px-6 py-2.5 rounded-xl bg-emerald-600 text-white font-medium text-sm shadow-sm hover:bg-emerald-700 hover:shadow disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
            >
              {saveWniosekMutation.isLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Zapisywanie...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>{id ? 'Zaktualizuj wniosek' : 'Złóż wniosek'}</span>
                </>
              )}
            </button>
          </div>
        )}
      </form>
      )}

      {activeSection === 'akceptacje' && pokazZakladkeAkceptacje && (
        <form
          onSubmit={handleSaveAkceptacje}
          className="bg-white shadow rounded-lg p-6 sm:p-8 print:shadow-none border-t border-gray-100 overflow-x-hidden"
        >
          <div className="max-w-4xl mx-auto w-full min-w-0 space-y-10">
            <h2 className="text-center text-base sm:text-lg font-semibold text-gray-900">
              Rachunek sprawdzono pod względem:
            </h2>

            {/* Dwie równe kolumny jak na formularzu papierowym */}
            <div className="rounded-lg border border-gray-400 overflow-hidden bg-white">
              <div className="grid grid-cols-1 md:grid-cols-2 md:divide-x md:divide-gray-300">
                <div className="min-w-0 p-6 sm:p-8 space-y-6 border-b md:border-b-0 border-gray-300">
                  <p className="text-center text-sm font-semibold text-red-600 tracking-wide">
                    Merytorycznym
                  </p>
                  {mozeEdytowacAkceptacjeFormalne && !mozeEdytowacAkceptacjeMerytorycznie && (
                    <p className="text-center text-xs text-gray-500">Wypełnia przełożony</p>
                  )}
                  <div className="space-y-2">
                    <label className="block text-xs text-gray-600 text-center">Data</label>
                    {mozeEdytowacAkceptacjeMerytorycznie ? (
                      <input
                        type="date"
                        value={formData.sprawdzenieMerytoryczne?.data || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            sprawdzenieMerytoryczne: {
                              ...formData.sprawdzenieMerytoryczne,
                              data: e.target.value
                            }
                          })
                        }
                        className="w-full max-w-[220px] mx-auto block rounded border border-gray-400 px-3 py-2 text-sm"
                      />
                    ) : (
                      <p className="text-center text-sm text-gray-900 min-h-[2.5rem] flex items-center justify-center">
                        {formData.sprawdzenieMerytoryczne?.data
                          ? new Date(formData.sprawdzenieMerytoryczne.data).toLocaleDateString('pl-PL')
                          : '—'}
                      </p>
                    )}
                  </div>
                  <div
                    ref={merytorycznyPodpisRef}
                    className={`min-w-0 space-y-2 pt-2 ${
                      podpisHighlight === 'merytoryczny' ? 'delegacja-podpis-miganie' : ''
                    }`}
                  >
                    <span className="block text-xs text-gray-600 text-center">Podpis</span>
                    <div className="flex justify-center overflow-hidden">
                      {mozeEdytowacAkceptacjeMerytorycznie ? (
                        <SignaturePad
                          onSignatureChange={(sig) => {
                            if (podpisHighlight === 'merytoryczny') setPodpisHighlight(null);
                            setFormData({
                              ...formData,
                              sprawdzenieMerytoryczne: {
                                ...formData.sprawdzenieMerytoryczne,
                                podpis: sig,
                                imieNazwisko: ''
                              }
                            });
                          }}
                          width={252}
                          height={108}
                          variant="compact"
                          signature={formData.sprawdzenieMerytoryczne?.podpis || undefined}
                          signerName={formatSignerNameUnderPad(
                            formData.sprawdzenieMerytoryczne?.podpis,
                            formData.sprawdzenieMerytoryczne?.imieNazwisko ||
                              w?.sprawdzenieMerytoryczne?.imieNazwisko,
                            user
                          )}
                        />
                      ) : (
                        formData.sprawdzenieMerytoryczne?.podpis && (
                          <div className="flex flex-col items-center">
                            <Podpis
                              value={formData.sprawdzenieMerytoryczne.podpis}
                              alt="Podpis merytoryczny"
                              className="max-h-28 max-w-[252px] object-contain"
                            />
                            {(formData.sprawdzenieMerytoryczne?.imieNazwisko ||
                              w?.sprawdzenieMerytoryczne?.imieNazwisko) && (
                              <p className="mt-2 text-sm font-medium text-gray-900 text-center max-w-[252px]">
                                {formData.sprawdzenieMerytoryczne?.imieNazwisko ||
                                  w?.sprawdzenieMerytoryczne?.imieNazwisko}
                              </p>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>

                <div
                  ref={formalnyBlokRef}
                  className={`min-w-0 p-6 sm:p-8 space-y-6 ${
                    podpisHighlight === 'formalny' ? 'delegacja-podpis-miganie' : ''
                  }`}
                >
                  <p className="text-center text-sm font-semibold text-red-600 tracking-wide">
                    Formalnym i rachunkowym
                  </p>
                  {mozeEdytowacAkceptacjeMerytorycznie && !mozeEdytowacAkceptacjeFormalne && (
                    <p className="text-center text-xs text-gray-500">Wypełnia HR lub Księgowość</p>
                  )}
                  <div className="space-y-2">
                    <label className="block text-xs text-gray-600 text-center">Data</label>
                    {mozeEdytowacAkceptacjeFormalne ? (
                      <input
                        type="date"
                        value={formData.sprawdzenieFormalne?.data || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            sprawdzenieFormalne: {
                              ...formData.sprawdzenieFormalne,
                              data: e.target.value
                            }
                          })
                        }
                        className="w-full max-w-[220px] mx-auto block rounded border border-gray-400 px-3 py-2 text-sm"
                      />
                    ) : (
                      <p className="text-center text-sm text-gray-900 min-h-[2.5rem] flex items-center justify-center">
                        {formData.sprawdzenieFormalne?.data
                          ? new Date(formData.sprawdzenieFormalne.data).toLocaleDateString('pl-PL')
                          : '—'}
                      </p>
                    )}
                  </div>
                  <div className="min-w-0 space-y-2 pt-2">
                    <span className="block text-xs text-gray-600 text-center">Podpis</span>
                    <div className="flex justify-center overflow-hidden">
                      {mozeEdytowacAkceptacjeFormalne ? (
                        <SignaturePad
                          onSignatureChange={(sig) => {
                            if (podpisHighlight === 'formalny') setPodpisHighlight(null);
                            setFormData({
                              ...formData,
                              sprawdzenieFormalne: {
                                ...formData.sprawdzenieFormalne,
                                podpis: sig,
                                imieNazwisko: ''
                              }
                            });
                          }}
                          width={252}
                          height={108}
                          variant="compact"
                          signature={formData.sprawdzenieFormalne?.podpis || undefined}
                          signerName={formatSignerNameUnderPad(
                            formData.sprawdzenieFormalne?.podpis,
                            formData.sprawdzenieFormalne?.imieNazwisko ||
                              w?.sprawdzenieFormalne?.imieNazwisko,
                            user
                          )}
                        />
                      ) : (
                        formData.sprawdzenieFormalne?.podpis && (
                          <div className="flex flex-col items-center">
                            <Podpis
                              value={formData.sprawdzenieFormalne.podpis}
                              alt="Podpis formalny"
                              className="max-h-28 max-w-[252px] object-contain"
                            />
                            {(formData.sprawdzenieFormalne?.imieNazwisko ||
                              w?.sprawdzenieFormalne?.imieNazwisko) && (
                              <p className="mt-2 text-sm font-medium text-gray-900 text-center max-w-[252px]">
                                {formData.sprawdzenieFormalne?.imieNazwisko ||
                                  w?.sprawdzenieFormalne?.imieNazwisko}
                              </p>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Zatwierdzono na sumę — jedna linia */}
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-2 text-sm text-gray-900">
              <span className="font-medium">Zatwierdzono na sumę:</span>
              {mozeEdytowacAkceptacjeFormalne ? (
                <>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.zatwierdzenieKwota}
                    onChange={(e) =>
                      setFormData({ ...formData, zatwierdzenieKwota: e.target.value })
                    }
                    className="w-32 border-0 border-b border-gray-800 bg-transparent px-1 py-0.5 text-sm font-medium tabular-nums focus:outline-none focus:ring-0"
                    placeholder="…………"
                  />
                  <span className="font-medium">zł</span>
                </>
              ) : (
                <>
                  <span className="font-semibold tabular-nums border-b border-dotted border-gray-500 min-w-[6rem] inline-block pb-0.5">
                    {formData.zatwierdzenieKwota !== '' && formData.zatwierdzenieKwota != null
                      ? parseFloat(formData.zatwierdzenieKwota).toLocaleString('pl-PL', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })
                      : '…………'}
                  </span>
                  <span className="font-medium">zł</span>
                </>
              )}
            </div>
            {formData.zatwierdzenieSlownie && (
              <p className="text-sm text-gray-600 italic mt-2">
                Słownie: <span className="not-italic text-gray-900">{formData.zatwierdzenieSlownie}</span>
              </p>
            )}

            {/* Dolny rząd: Data (linia kropkowana) | Podpis akceptującego */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 pt-4">
              <div className="min-w-0 flex flex-col justify-end">
                {mozeEdytowacAkceptacjeFormalne ? (
                  <input
                    type="date"
                    value={formData.zatwierdzenieData || ''}
                    onChange={(e) => setFormData({ ...formData, zatwierdzenieData: e.target.value })}
                    className="w-full bg-transparent border-0 border-b border-dotted border-gray-500 px-0 py-1 text-sm text-center focus:outline-none focus:ring-0"
                  />
                ) : (
                  <div className="border-b border-dotted border-gray-500 min-h-[1.75rem] text-center text-sm text-gray-900 flex items-end justify-center pb-1">
                    {formData.zatwierdzenieData
                      ? new Date(formData.zatwierdzenieData).toLocaleDateString('pl-PL')
                      : ''}
                  </div>
                )}
                <p className="text-center text-xs text-gray-600 mt-2">Data</p>
              </div>
              <div className="min-w-0 flex flex-col items-center md:items-stretch">
                <div className="w-full flex justify-center md:justify-end overflow-hidden">
                  {mozeEdytowacAkceptacjeFormalne ? (
                    <SignaturePad
                      onSignatureChange={(sig) =>
                        setFormData({
                          ...formData,
                          zatwierdzeniePodpisy: sig,
                          zatwierdzenieImieNazwiskoAkceptujacego: ''
                        })
                      }
                      width={300}
                      height={110}
                      variant="compact"
                      signature={formData.zatwierdzeniePodpisy || undefined}
                      signerName={formatSignerNameUnderPad(
                        formData.zatwierdzeniePodpisy,
                        formData.zatwierdzenieImieNazwiskoAkceptujacego ||
                          w?.zatwierdzenieImieNazwiskoAkceptujacego,
                        user
                      )}
                    />
                  ) : (
                    podpisAkceptujacegoSrc && (
                      <div className="flex flex-col items-center md:items-end w-full">
                        <Podpis
                          value={podpisAkceptujacegoSrc}
                          alt="Podpis akceptującego"
                          className="max-h-28 max-w-full object-contain"
                        />
                        {nazwaPodPodpisemAkceptujacego && (
                          <p className="mt-2 text-sm font-medium text-gray-900 text-center md:text-right w-full">
                            {nazwaPodPodpisemAkceptujacego}
                          </p>
                        )}
                      </div>
                    )
                  )}
                </div>
                <p className="text-center md:text-right text-xs text-gray-600 mt-2">Podpis akceptującego</p>
              </div>
            </div>

            {(mozeZapisacAkceptacje || mozeZwrocDoPracownika) && (
              <div className="flex flex-col gap-3 print:hidden pt-4 border-t border-gray-200">
                {mozeZwrocDoPracownika && (
                  <button
                    type="button"
                    onClick={() => setZwrotModalOpen(true)}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border-2 border-orange-400 bg-orange-50 text-orange-950 hover:bg-orange-100 font-semibold text-sm"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Zwróć do pracownika do poprawy
                  </button>
                )}
                {mozeZapisacAkceptacje && (
                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setActiveSection('rachunek')}
                      className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium text-sm"
                    >
                      Wróć do rachunku
                    </button>
                    <button
                      type="submit"
                      disabled={saveAkceptacjeMutation.isLoading}
                      className="px-6 py-2.5 rounded-lg bg-red-600 text-white font-medium text-sm shadow-sm hover:bg-red-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    >
                      {saveAkceptacjeMutation.isLoading ? (
                        <>
                          <LoadingSpinner size="sm" />
                          Zapisywanie…
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          Zapisz akceptacje
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </form>
      )}

      {zwrotModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 print:hidden">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl border border-gray-200">
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Zwróć rachunek do pracownika</h3>
              <p className="mt-1 text-sm text-gray-600">
                Opisz, co należy poprawić w rachunku kosztów. Pracownik otrzyma powiadomienie i e-mail z Twoimi uwagami.
              </p>
            </div>
            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Informacja dla pracownika *
              </label>
              <textarea
                value={komentarzZwrotu}
                onChange={(e) => setKomentarzZwrotu(e.target.value)}
                rows={5}
                maxLength={2000}
                placeholder="Np. błędna kwota w wierszu 2, brak daty przyjezdu, dołącz skan paragonu za parking…"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <p className="mt-1 text-xs text-gray-500 text-right">{komentarzZwrotu.length}/2000</p>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 border-t border-gray-200 px-6 py-4">
              <button
                type="button"
                onClick={() => {
                  setZwrotModalOpen(false);
                  setKomentarzZwrotu('');
                }}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
              >
                Anuluj
              </button>
              <button
                type="button"
                disabled={!komentarzZwrotu.trim() || zwrotDoPracownikaMutation.isLoading}
                onClick={() => zwrotDoPracownikaMutation.mutate()}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 text-sm font-medium"
              >
                {zwrotDoPracownikaMutation.isLoading ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Wysyłanie…
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4" />
                    Zwróć do pracownika
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {zwrotPolecenieModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 print:hidden">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl border border-gray-200">
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Zwróć wniosek do poprawy</h3>
              <p className="mt-1 text-sm text-gray-600">
                Wniosek wróci do pracownika przed podpisem polecenia. Opisz, co należy poprawić — pracownik otrzyma powiadomienie i e-mail.
              </p>
            </div>
            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Informacja dla pracownika *
              </label>
              <textarea
                value={komentarzZwrotu}
                onChange={(e) => setKomentarzZwrotu(e.target.value)}
                rows={5}
                maxLength={2000}
                placeholder="Np. błędne daty wyjazdu, popraw miejsce delegacji, uzupełnij cel wyjazdu…"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <p className="mt-1 text-xs text-gray-500 text-right">{komentarzZwrotu.length}/2000</p>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 border-t border-gray-200 px-6 py-4">
              <button
                type="button"
                onClick={() => {
                  setZwrotPolecenieModalOpen(false);
                  setKomentarzZwrotu('');
                }}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
              >
                Anuluj
              </button>
              <button
                type="button"
                disabled={!komentarzZwrotu.trim() || zwrotPoleceniaMutation.isLoading}
                onClick={() => zwrotPoleceniaMutation.mutate()}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 text-sm font-medium"
              >
                {zwrotPoleceniaMutation.isLoading ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Wysyłanie…
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4" />
                    Zwróć do poprawy
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Wydruk A4: pełny wniosek (ukryty na ekranie, widoczny tylko w podglądzie druku) */}
      <div className="hidden print:block wniosek-delegacyjny-print bg-white text-gray-900">
        <div className="wniosek-delegacyjny-print-inner mx-auto max-w-[190mm]">
          <div className="mb-2 grid grid-cols-[minmax(0,14rem)_1fr_minmax(0,14rem)] items-center gap-x-2">
            <div className="flex justify-start min-w-0">
              <img
                src={logoImage}
                alt="Farm Connect"
                className="wniosek-delegacyjny-print-logo h-[4.75rem] w-auto max-h-[4.75rem] max-w-full object-contain object-left"
              />
            </div>
            <h1 className="text-center text-[14pt] font-bold mb-0 mt-0 px-1 min-w-0">
              Polecenie wyjazdu służbowego
            </h1>
            <div className="min-w-0" aria-hidden="true" />
          </div>
          {/* Kompakt: pola w dwóch kolumnach */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10pt] leading-snug">
            <div className="flex gap-1.5 min-w-0">
              <span className="shrink-0 font-semibold text-gray-800">Z dnia:</span>
              <span className="tabular-nums">{fmtDateVal(formData.dataPolecenia)}</span>
            </div>
            <div className="flex gap-1.5 justify-end text-right min-w-0">
              <span className="shrink-0 font-semibold text-gray-800">Dla:</span>
              <span className="font-medium break-words text-right">
                {[formData.pracownikImie, formData.pracownikNazwisko].filter(Boolean).join(' ') || '—'}
              </span>
            </div>
            <div className="flex gap-1.5 min-w-0">
              <span className="shrink-0 font-semibold text-gray-800">Stanowisko:</span>
              <span>{formData.stanowisko || '—'}</span>
            </div>
            <div className="flex gap-1.5 min-w-0">
              <span className="shrink-0 font-semibold text-gray-800">Do (miejsce):</span>
              <span>{formData.miejsceDelegacji || '—'}</span>
            </div>
            <div className="flex gap-1.5 min-w-0">
              <span className="shrink-0 font-semibold text-gray-800">Na czas od – do:</span>
              <span className="tabular-nums">
                {fmtDateVal(formData.dataOd)} – {fmtDateVal(formData.dataDo)}
                {formData.liczbaDni ? ` (${formData.liczbaDni} dni)` : ''}
              </span>
            </div>
            <div className="flex gap-1.5 min-w-0">
              <span className="shrink-0 font-semibold text-gray-800">W celu:</span>
              <span className="whitespace-pre-wrap break-words">{formData.celDelegacji || '—'}</span>
            </div>
            <div className="col-span-2 flex gap-1.5 border-t border-gray-500 pt-1 mt-0.5">
              <span className="shrink-0 font-semibold text-gray-800">Środki lokomocji:</span>
              <span className="break-words">{srodekTransportuDoDruku}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-2 items-end">
            <div>
              <div className="border-b-2 border-dotted border-gray-900 min-h-[1rem] flex items-end justify-center pb-0.5">
                <span className="text-[10pt] tabular-nums">
                  {fmtDateVal(w?.dataPodpisuPrzełożonego || formData.dataAkceptacjiPolecenia)}
                </span>
              </div>
              <p className="text-center text-[9pt] text-gray-800 mt-0.5 font-medium">Data</p>
            </div>
            <div className="flex flex-col items-end">
              {podpisPrzełożonegoSrcDruk ? (
                <>
                  <img
                    src={podpisPrzełożonegoSrcDruk}
                    alt=""
                    className="max-h-[48px] max-w-full object-contain"
                  />
                  {(w?.podpisPrzełożonegoImieNazwisko || nazwaPrzełożonegoDruk) && (
                    <p className="text-[9.5pt] font-semibold text-center mt-0.5 w-full text-gray-900 leading-tight">
                      {w?.podpisPrzełożonegoImieNazwisko || nazwaPrzełożonegoDruk}
                    </p>
                  )}
                </>
              ) : (
                <div className="min-h-[48px] w-full border-2 border-dotted border-gray-400 rounded-sm" />
              )}
              <p className="text-[9pt] text-gray-800 mt-0.5 w-full text-right font-medium leading-tight">
                Podpis akceptującego (polecenie)
              </p>
            </div>
          </div>

          <hr className="border-0 border-t-[3px] border-blue-800 my-2" />

          <h2 className="text-center text-[12pt] font-bold mt-1 mb-1">
            Rachunek kosztów podróży służbowej
          </h2>
          <table className="wniosek-delegacyjny-print-table w-full border-collapse border-2 border-gray-900 table-fixed">
            <colgroup>
              <col />
              <col />
              <col />
              <col />
              <col />
              <col />
              <col />
              <col />
              <col />
            </colgroup>
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-gray-900 px-1 py-0.5" colSpan={3}>
                  Wyjazd
                </th>
                <th className="border border-gray-900 px-1 py-0.5" colSpan={3}>
                  Przyjazd
                </th>
                <th className="border border-gray-900 px-1 py-0.5 align-middle" rowSpan={2}>
                  Środki lokomocji
                </th>
                <th className="border border-gray-900 px-1 py-0.5" colSpan={2}>
                  Koszty przejazdu
                </th>
              </tr>
              <tr className="bg-gray-100">
                <th className="border border-gray-900 font-semibold px-1 py-0.5">miejsc.</th>
                <th className="border border-gray-900 font-semibold px-1 py-0.5">data</th>
                <th className="border border-gray-900 font-semibold px-1 py-0.5">godz.</th>
                <th className="border border-gray-900 font-semibold px-1 py-0.5">miejsc.</th>
                <th className="border border-gray-900 font-semibold px-1 py-0.5">data</th>
                <th className="border border-gray-900 font-semibold px-1 py-0.5">godz.</th>
                <th className="border border-gray-900 font-semibold px-1 py-0.5">zł</th>
                <th className="border border-gray-900 font-semibold px-1 py-0.5">gr</th>
              </tr>
            </thead>
            <tbody>
              {listaWyjazdowDruk.map((wp, idx) => (
                <tr key={idx} className="align-middle">
                  <td className="border border-gray-900 px-1">{wp?.wyjazdMiejscowosc || '\u00A0'}</td>
                  <td className="border border-gray-900 px-1 tabular-nums">
                    {wp?.wyjazdData ? fmtDateVal(wp.wyjazdData) : '\u00A0'}
                  </td>
                  <td className="border border-gray-900 px-1 tabular-nums">
                    {wp?.wyjazdGodzina || '\u00A0'}
                  </td>
                  <td className="border border-gray-900 px-1">{wp?.przyjazdMiejscowosc || '\u00A0'}</td>
                  <td className="border border-gray-900 px-1 tabular-nums">
                    {wp?.przyjazdData ? fmtDateVal(wp.przyjazdData) : '\u00A0'}
                  </td>
                  <td className="border border-gray-900 px-1 tabular-nums">
                    {wp?.przyjazdGodzina || '\u00A0'}
                  </td>
                  <td className="border border-gray-900 px-1">{wp?.srodekLokomocji || '\u00A0'}</td>
                  <td className="border border-gray-900 px-1 text-right tabular-nums">
                    {wp?.kosztPrzejazdu?.zl != null && wp?.kosztPrzejazdu?.zl !== ''
                      ? wp.kosztPrzejazdu.zl
                      : '\u00A0'}
                  </td>
                  <td className="border border-gray-900 px-1 text-right tabular-nums">
                    {wp?.kosztPrzejazdu?.gr != null && wp?.kosztPrzejazdu?.gr !== ''
                      ? wp.kosztPrzejazdu.gr
                      : '\u00A0'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10pt] mt-1 font-semibold leading-tight">
            Suma ogółem do wpłaty/zwrotu:{' '}
            <span className="tabular-nums font-bold">{formatujSumeWplatyZwrotu(sumaWplatyZwrotu)}</span>
          </p>

          <div className="flex flex-wrap items-end gap-x-2 gap-y-1 mt-1.5 border-t border-gray-400 pt-1.5 text-[10pt] leading-tight">
            <span className="font-medium">Niniejszym rachunek przedkładam:</span>
            <span className="border-b-2 border-gray-900 min-w-[5rem] inline-block pb-0.5 tabular-nums text-center">
              {formData.rachunekDataPrzedlozenia ? fmtDateVal(formData.rachunekDataPrzedlozenia) : ''}
            </span>
            <span className="font-semibold">podpis pracownika</span>
            {formData.rachunekPodpisPracownika || w?.rachunekPodpisPracownika ? (
              <img
                src={formData.rachunekPodpisPracownika || w?.rachunekPodpisPracownika}
                alt=""
                className="max-h-[40px] inline-block align-bottom ml-1 border border-gray-400 rounded-sm bg-white"
              />
            ) : null}
          </div>
          {(w?.rachunekPodpisPracownikaImieNazwisko ||
            [formData.pracownikImie, formData.pracownikNazwisko].filter(Boolean).join(' ')) && (
            <p className="text-[10pt] mt-0.5 font-semibold text-gray-900 leading-tight">
              {w?.rachunekPodpisPracownikaImieNazwisko ||
                [formData.pracownikImie, formData.pracownikNazwisko].filter(Boolean).join(' ')}
            </p>
          )}

          {pokazZakladkeAkceptacje && (
            <>
              <p className="text-center text-[10pt] font-bold mt-2 mb-0.5 text-gray-900 leading-tight">
                Rachunek sprawdzono pod względem:
              </p>
              <div className="grid grid-cols-2 gap-0 border-2 border-gray-900 rounded-sm overflow-hidden">
                <div className="border-r-2 border-gray-900 px-1.5 py-1 bg-gray-50">
                  <p className="text-center text-[9.5pt] font-bold text-red-800 leading-tight">Merytorycznym</p>
                  <p className="text-[9pt] text-center mt-0.5 text-gray-900 leading-tight">
                    <span className="font-semibold">Data:</span>{' '}
                    {formData.sprawdzenieMerytoryczne?.data
                      ? fmtDateVal(formData.sprawdzenieMerytoryczne.data)
                      : '—'}
                  </p>
                  {formData.sprawdzenieMerytoryczne?.podpis || w?.sprawdzenieMerytoryczne?.podpis ? (
                    <div className="flex flex-col items-center mt-0.5">
                      <Podpis
                        value={formData.sprawdzenieMerytoryczne?.podpis || w?.sprawdzenieMerytoryczne?.podpis}
                        alt="Podpis merytoryczny"
                        size="sm"
                        className="max-h-[36px] max-w-[95%] object-contain"
                      />
                      <span className="text-[9pt] font-semibold mt-0.5 text-gray-900 leading-tight">
                        {formData.sprawdzenieMerytoryczne?.imieNazwisko ||
                          w?.sprawdzenieMerytoryczne?.imieNazwisko ||
                          ''}
                      </span>
                    </div>
                  ) : (
                    <div className="min-h-[22px]" />
                  )}
                </div>
                <div className="px-1.5 py-1 bg-gray-50">
                  <p className="text-center text-[9.5pt] font-bold text-red-800 leading-tight">
                    Formalnym i rachunkowym
                  </p>
                  <p className="text-[9pt] text-center mt-0.5 text-gray-900 leading-tight">
                    <span className="font-semibold">Data:</span>{' '}
                    {formData.sprawdzenieFormalne?.data
                      ? fmtDateVal(formData.sprawdzenieFormalne.data)
                      : '—'}
                  </p>
                  {formData.sprawdzenieFormalne?.podpis || w?.sprawdzenieFormalne?.podpis ? (
                    <div className="flex flex-col items-center mt-0.5">
                      <Podpis
                        value={formData.sprawdzenieFormalne?.podpis || w?.sprawdzenieFormalne?.podpis}
                        alt="Podpis formalny"
                        size="sm"
                        className="max-h-[36px] max-w-[95%] object-contain"
                      />
                      <span className="text-[9pt] font-semibold mt-0.5 text-gray-900 leading-tight">
                        {formData.sprawdzenieFormalne?.imieNazwisko ||
                          w?.sprawdzenieFormalne?.imieNazwisko ||
                          ''}
                      </span>
                    </div>
                  ) : (
                    <div className="min-h-[22px]" />
                  )}
                </div>
              </div>

              <div className="text-[10pt] mt-1.5 leading-tight">
                <span className="font-bold">Zatwierdzono na sumę:</span>{' '}
                {formData.zatwierdzenieKwota !== '' && formData.zatwierdzenieKwota != null
                  ? `${parseFloat(formData.zatwierdzenieKwota).toLocaleString('pl-PL', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })} zł`
                  : '—'}
                {formData.zatwierdzenieSlownie ? (
                  <span className="block text-[9pt] italic text-gray-800 mt-0.5 leading-snug">
                    Słownie: {formData.zatwierdzenieSlownie}
                  </span>
                ) : null}
              </div>

              <div className="grid grid-cols-2 gap-4 mt-1.5 items-end">
                <div>
                  <div className="border-b-2 border-dotted border-gray-900 min-h-[1rem] flex items-end justify-center pb-0.5">
                    <span className="text-[10pt] tabular-nums">
                      {formData.zatwierdzenieData ? fmtDateVal(formData.zatwierdzenieData) : ''}
                    </span>
                  </div>
                  <p className="text-center text-[9pt] text-gray-800 mt-0.5 font-medium leading-tight">Data</p>
                </div>
                <div className="flex flex-col items-end">
                  {podpisAkceptujacegoSrc ? (
                    <>
                      <Podpis
                        value={podpisAkceptujacegoSrc}
                        alt="Podpis akceptującego"
                        size="sm"
                        className="max-h-[44px] max-w-full object-contain"
                      />
                      {nazwaPodPodpisemAkceptujacego ? (
                        <p className="text-[9.5pt] font-semibold mt-0.5 text-right w-full text-gray-900 leading-tight">
                          {nazwaPodPodpisemAkceptujacego}
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <div className="min-h-[44px] w-full border-2 border-dotted border-gray-400 rounded-sm" />
                  )}
                  <p className="text-[9pt] text-gray-800 mt-0.5 w-full text-right font-medium leading-tight">
                    Podpis akceptującego
                  </p>
                </div>
              </div>
            </>
          )}

          <hr className="border-0 border-t border-gray-500 mt-2" />
          <p className="text-[8pt] text-gray-600 text-center mt-1 leading-tight">
            Wygenerowano z systemu Farm Connect ·{' '}
            {new Date().toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' })}
          </p>
        </div>
      </div>
    </>
  );
};

export default WniosekDelegacyjny;
